import React, { useState, useMemo } from 'react'
import InventoryItem from './InventoryItem'

const hasProvisionTag = (item) =>
  Array.isArray(item?.tags) && item.tags.map((t) => String(t)).includes('provision')

export default function CharacterInventory({
  inventory,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  getDragClasses,
  onMoveItem,
  variant = 'default',
}) {
  const isEmbedded = variant === 'embedded'
  const [filter, setFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')

  const itemMatches = (item) => {
    if (!item) return false
    if (filter === 'provision') {
      if (!hasProvisionTag(item)) return false
    } else if (filter !== 'all' && item.type !== filter) {
      return false
    }
    if (searchTerm && !item.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false
    }
    return true
  }

  const filteredEntries = useMemo(() => {
    const raw = inventory.items || []
    const matched = []
    for (let i = 0; i < raw.length; i++) {
      const item = raw[i]
      if (!itemMatches(item)) continue
      matched.push({ item, bagIndex: i })
    }
    return matched
  }, [inventory.items, filter, searchTerm])

  const isFiltering = filter !== 'all' || Boolean(searchTerm)

  // Grid: sin filtro = slots reales; con filtro = ítems filtrados + vacíos (sin drop en padding)
  const inventorySlots = useMemo(() => {
    const maxSlots = inventory.maxSlots || 30
    if (!isFiltering) {
      const slots = []
      for (let i = 0; i < maxSlots; i++) {
        slots.push({ item: inventory.items?.[i] || null, bagIndex: i, isPadding: false })
      }
      return slots
    }
    const slots = filteredEntries.map((e) => ({
      item: e.item,
      bagIndex: e.bagIndex,
      isPadding: false,
    }))
    while (slots.length < maxSlots) {
      slots.push({ item: null, bagIndex: null, isPadding: true })
    }
    return slots
  }, [inventory.items, inventory.maxSlots, isFiltering, filteredEntries])

  const handleDragOver = (e, bagIndex, isPadding) => {
    if (isPadding || bagIndex == null) return
    if (onDragOver) onDragOver(e, 'inventory', bagIndex)
  }

  const handleDragEnter = (e, bagIndex, isPadding) => {
    if (isPadding || bagIndex == null) return
    if (onDragEnter) onDragEnter(e, 'inventory', bagIndex)
  }

  const handleDragLeave = (e) => {
    if (onDragLeave) onDragLeave(e)
  }

  const handleDrop = (e, bagIndex, isPadding) => {
    if (isPadding || bagIndex == null) return
    if (onDrop) onDrop(e, 'inventory', bagIndex)
  }

  const handleItemClick = () => {}

  const getFilterOptions = () => {
    const types = new Set()
    inventory.items?.forEach((item) => {
      if (item && item.type) types.add(item.type)
    })
    return Array.from(types)
  }

  const hasAnyProvision = (inventory.items || []).some(hasProvisionTag)

  const getTypeDisplayName = (type) => {
    const typeNames = {
      weapon: 'Armas',
      armor: 'Armaduras',
      consumable: 'Consumibles',
      misc: 'Varios',
      helmet: 'Cascos',
      chest: 'Pecho',
      hands: 'Manos',
      legs: 'Piernas',
      feet: 'Pies',
      provision: 'Provisiones',
    }
    return typeNames[type] || type
  }

  return (
    <div className={`inventory__stash${isEmbedded ? ' char-equipo__stash' : ''}`}>
      {!isEmbedded ? (
        <div className="inventory__equipment-title">
          <p>Inventario</p>
        </div>
      ) : (
        <div className="char-equipo__stash-header char-section-header char-equipo__section-header">
          <span className="char-section-header__ornament" aria-hidden="true" />
          <h3 className="char-section-header__title">Mochila</h3>
          <span className="char-section-header__ornament char-section-header__ornament--right" aria-hidden="true" />
        </div>
      )}

      <div className="inventory-controls">
        <div className="inventory-search">
          <input
            type="text"
            placeholder="Buscar items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="inventory-filters">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
            <option value="all">Todos</option>
            {hasAnyProvision ? <option value="provision">Provisiones</option> : null}
            {getFilterOptions().map((type) => (
              <option key={type} value={type}>
                {getTypeDisplayName(type)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="inventory-info">
        <span className="inventory-count">
          {inventory.items?.filter(Boolean).length || 0} / {inventory.maxSlots || 30}
        </span>
        {isFiltering && (
          <span className="search-results">{filteredEntries.length} resultados</span>
        )}
      </div>

      <div className="inventory-grid">
        {inventorySlots.map((slot, visualIndex) => {
          const { item, bagIndex, isPadding } = slot
          const dndIndex = bagIndex != null ? bagIndex : visualIndex
          return (
            <div
              key={isPadding ? `pad-${visualIndex}` : `bag-${bagIndex}`}
              className={`inventory-slot ${
                !isPadding ? getDragClasses('inventory', dndIndex) : ''
              } ${!item ? 'empty-slot' : ''} ${isPadding ? 'inventory-slot--padding' : ''}`}
              onDragOver={(e) => handleDragOver(e, bagIndex, isPadding)}
              onDragEnter={(e) => handleDragEnter(e, bagIndex, isPadding)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, bagIndex, isPadding)}
              data-slot-index={bagIndex ?? ''}
            >
              <div className="inventory__equipment-slot">
                <InventoryItem
                  item={item}
                  index={dndIndex}
                  sourceType="inventory"
                  onDragStart={isPadding ? undefined : onDragStart}
                  onDragEnd={onDragEnd}
                  onClick={handleItemClick}
                  isDraggable={!isPadding && Boolean(item)}
                  className="inventory-item-slot"
                />
              </div>
            </div>
          )
        })}
      </div>

      {(!inventory.items || inventory.items.filter(Boolean).length === 0) && (
        <div className="inventory-empty">
          <p>El inventario está vacío</p>
        </div>
      )}

      {isFiltering && filteredEntries.length === 0 && inventory.items?.some(Boolean) && (
        <div className="inventory-no-results">
          <p>
            {filter === 'provision'
              ? 'No hay provisiones en la mochila'
              : searchTerm
                ? `No se encontraron items que coincidan con "${searchTerm}"`
                : 'No hay items con este filtro'}
          </p>
        </div>
      )}
    </div>
  )
}
